import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Target, ChevronRight, Loader2, ShieldCheck, Lightbulb, Database,
  CheckCircle2, PauseCircle, Ban, RefreshCw, Circle, GitBranch, MessageCircle,
  Sparkles,
} from "lucide-react";
import AssignTaskModal from "../components/AssignTaskModal";

const CONFIDENCE = {
  data_backed: { label: "Data-Backed", cls: "bg-status-onTrack-bg text-status-onTrack-text border-status-onTrack-border", Icon: Database },
  strategic:   { label: "Strategic",   cls: "bg-status-drifting-bg text-status-drifting-text border-status-drifting-border", Icon: ShieldCheck },
  exploratory: { label: "Exploratory", cls: "bg-persona-ceo-tint text-persona-ceo border-persona-ceo-soft",    Icon: Lightbulb },
};
const STATUS = {
  not_started: { label: "Not Started", cls: "bg-ink-100 text-ink-600", Icon: Circle },
  in_progress: { label: "In Progress", cls: "bg-blue-100 text-blue-800", Icon: RefreshCw },
  completed:   { label: "Completed",   cls: "bg-status-onTrack-bg text-status-onTrack-text", Icon: CheckCircle2 },
  deferred:    { label: "Deferred",    cls: "bg-status-drifting-bg text-status-drifting-text", Icon: PauseCircle },
  blocked:     { label: "Blocked",     cls: "bg-status-critical-bg text-status-critical-text", Icon: Ban },
};

function ConfidenceBadge({ level }) {
  const c = CONFIDENCE[level] || CONFIDENCE.strategic;
  const { Icon } = c;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${c.cls}`}
          data-testid={`confidence-${level}`}>
      <Icon className="w-3 h-3" /> {c.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.not_started;
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

const SOURCE_ROLE_LABEL = { cfo: "CFO", ceo: "CEO", cto: "CTO", cmo: "CMO" };

const SEAT_STYLE = {
  CEO: "bg-persona-ceo-tint text-persona-ceo border-persona-ceo-soft",
  CFO: "bg-persona-cfo-tint text-persona-cfo border-persona-cfo-soft",
  CMO: "bg-persona-cmo-tint text-persona-cmo border-persona-cmo-soft",
  CTO: "bg-persona-cto-tint text-persona-cto border-persona-cto-soft",
  COO: "bg-persona-coo-tint text-persona-coo border-persona-coo-soft",
};

/**
 * C-ORE Task Intelligence (2026-08) — crisp "C-ORE ANALYSIS" section
 * inside the existing Task card. Founder never sees this analysis being
 * run; it just appears once ready. `ai_analysis_status` is:
 *   pending   → LLM analysis still running in the background
 *   completed → all ai_* fields populated, expandable
 *   failed    → analysis could not complete — section quietly hides its
 *               detail, never blocks the task itself
 */
function CoreAnalysisSection({ item }) {
  const [expanded, setExpanded] = useState(false);
  const status = item.ai_analysis_status;
  if (item.source !== "founder_created" || item.show_founder_badge === false || !status) return null;

  return (
    <div className="mt-3 rounded-lg border border-ink-100 bg-ink-50/50 overflow-hidden" data-testid={`core-analysis-${item.id}`}>
      <button
        type="button"
        onClick={() => status === "completed" && setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
        data-testid={`core-analysis-toggle-${item.id}`}
      >
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-blue-800">
          <Sparkles className="w-3 h-3" /> C-ORE Analysis
        </span>
        {status === "pending" && <span className="text-[11px] text-ink-400" data-testid={`core-analysis-pending-${item.id}`}>Analyzing…</span>}
        {status === "failed" && <span className="text-[11px] text-ink-400" data-testid={`core-analysis-failed-${item.id}`}>Unavailable</span>}
        {status === "completed" && (
          <ChevronRight className={`w-3.5 h-3.5 text-ink-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
        )}
      </button>
      {expanded && status === "completed" && (
        <div className="px-3 pb-3 space-y-2 text-[12px]" data-testid={`core-analysis-body-${item.id}`}>
          {item.ai_executive_contribution && (
            <div className="flex items-center gap-1.5">
              <span className="text-ink-400 font-semibold shrink-0">Executive Contribution:</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${SEAT_STYLE[item.ai_executive_contribution] || SEAT_STYLE.CEO}`}>
                {item.ai_executive_contribution}
              </span>
            </div>
          )}
          {item.ai_why_it_matters && (
            <div className="flex gap-1.5"><span className="text-ink-400 font-semibold shrink-0">Why it matters:</span><span className="text-ink-700">{item.ai_why_it_matters}</span></div>
          )}
          {item.ai_business_impact && (
            <div className="flex gap-1.5"><span className="text-ink-400 font-semibold shrink-0">Business impact:</span><span className="text-status-onTrack-text">{item.ai_business_impact}</span></div>
          )}
          {item.ai_risk_of_delay && (
            <div className="flex gap-1.5"><span className="text-ink-400 font-semibold shrink-0">Risk of delay:</span><span className="text-status-critical-text">{item.ai_risk_of_delay}</span></div>
          )}
          {item.ai_priority_assessment && (
            <div className="flex gap-1.5"><span className="text-ink-400 font-semibold shrink-0">Priority:</span><span className="text-blue-800 font-semibold">{item.ai_priority_assessment}</span></div>
          )}
        </div>
      )}
    </div>
  );
}

function TaskCard({ item, onAct, busy, onAssign }) {
  const navigate = useNavigate();
  const canAct = item.source_rule_id && item.requires_approval &&
                 ["not_started", "in_progress", "blocked"].includes(item.status);
  const canComplete = item.can_complete && item.status !== "completed";
  const canDismissCmo = item.source === "cmo_task" && item.status !== "completed";
  // "Assign Task via WhatsApp" reuses this exact same actionability gate —
  // no new definition of "actionable" is introduced by this feature.
  const canAssign = canAct || canComplete || canDismissCmo;
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-4 shadow-card hover:shadow-hover transition-shadow"
         data-testid={`exec-task-${item.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <ConfidenceBadge level={item.confidence} />
        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-ink-100 text-ink-600"
              data-testid={`exec-dept-${item.id}`}>{item.department}</span>
        {item.priority != null && (
          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-ink-100 text-ink-500">P{item.priority}</span>
        )}
        <StatusBadge status={item.status} />
        {item.source === "executive_recommendation" && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-persona-ceo-tint text-persona-ceo border border-persona-ceo-soft"
                data-testid={`exec-source-${item.id}`}>
            From {SOURCE_ROLE_LABEL[item.source_role] || item.source_role} Briefing
          </span>
        )}
        {item.source === "cmo_task" && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-persona-cmo-tint text-persona-cmo border border-persona-cmo-soft"
                data-testid={`exec-source-${item.id}`}>
            CMO Marketing{item.channel ? ` · ${item.channel}` : ""}
          </span>
        )}
        {item.source === "founder_created" && item.show_founder_badge !== false && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-ink-100 text-ink-600 border border-ink-200"
                data-testid={`exec-source-${item.id}`}>
            Founder Created
          </span>
        )}
        <span className="ml-auto text-[11px] text-ink-400">{item.due_timeline}</span>
      </div>

      <h4 className="mt-2.5 text-[15px] font-display font-bold text-blue-800" data-testid={`exec-title-${item.id}`}>{item.title}</h4>
      {item.detail && <p className="mt-1 text-[13px] text-ink-800"><span className="font-semibold">Do this: </span>{item.detail}</p>}

      <div className="mt-2.5 space-y-1.5 text-[12px]">
        {item.why && (
          <div className="flex gap-1.5"><span className="text-ink-400 font-semibold shrink-0">Why:</span><span className="text-ink-600">{item.why}</span></div>
        )}
        {item.related_goal && (
          <div className="flex gap-1.5" data-testid={`exec-goal-${item.id}`}>
            <span className="text-ink-400 font-semibold shrink-0">Goal:</span>
            <span className="inline-flex items-center gap-1 text-ink-700"><Target className="w-3 h-3 text-blue-800" />{item.related_goal}</span>
          </div>
        )}
        {item.expected_impact && (
          <div className="flex gap-1.5"><span className="text-ink-400 font-semibold shrink-0">Impact:</span><span className="text-status-onTrack-text">{item.expected_impact}</span></div>
        )}
      </div>

      {item.kpi_affected?.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {item.kpi_affected.map((k) => (
            <span key={k.metric_id} className="text-[11px] px-2 py-0.5 rounded-md bg-ink-50 text-ink-500 border border-ink-200">{k.label}</span>
          ))}
        </div>
      )}

      <CoreAnalysisSection item={item} />

      {(canAct || canComplete || canDismissCmo || item.module_path) && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {canAct && (
            <>
              <button onClick={() => onAct(item, "act")} disabled={busy}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-blue-800 text-white hover:bg-blue-900 disabled:opacity-50"
                data-testid={`exec-approve-${item.id}`}>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{busy ? "Working…" : "Approve & mark done"}
              </button>
              <button onClick={() => onAct(item, "dismiss")} disabled={busy}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-lg border border-ink-200 text-ink-600 hover:bg-ink-50 disabled:opacity-50"
                data-testid={`exec-dismiss-${item.id}`}>Dismiss</button>
            </>
          )}
          {canComplete && (
            <button onClick={() => onAct(item, "complete")} disabled={busy}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-status-onTrack text-white hover:bg-status-onTrack-text disabled:opacity-50"
              data-testid={`exec-complete-${item.id}`}>
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{busy ? "Working…" : "Mark done"}
            </button>
          )}
          {canDismissCmo && (
            <button onClick={() => onAct(item, "dismiss")} disabled={busy}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-lg border border-ink-200 text-ink-600 hover:bg-ink-50 disabled:opacity-50"
              data-testid={`exec-dismiss-${item.id}`}>Dismiss</button>
          )}
          {canAssign && (
            <button onClick={() => onAssign(item)}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-status-onTrack-border text-status-onTrack-text hover:bg-status-onTrack-bg"
              data-testid={`exec-assign-${item.id}`}>
              <MessageCircle className="w-3.5 h-3.5" /> Assign Task
            </button>
          )}
          {item.source === "cmo_task" && item.source_rule_id && (
            <button onClick={() => navigate(`/cmo/playbook?rule_id=${encodeURIComponent(item.source_rule_id)}`)}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-persona-cmo-soft text-persona-cmo hover:bg-persona-cmo-tint"
              data-testid={`exec-playbook-${item.id}`}>Open Playbook</button>
          )}
          {item.source === "founder_created" && item.playbook_id && item.source_rule_id && (
            <button onClick={() => navigate(`/cmo/playbook?rule_id=${encodeURIComponent(item.source_rule_id)}`)}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-persona-cmo-soft text-persona-cmo hover:bg-persona-cmo-tint"
              data-testid={`exec-playbook-${item.id}`}>Open Playbook</button>
          )}
          {item.module_path && item.owner_title && (
            <button onClick={() => navigate(item.module_path)}
              className="ml-auto text-[12px] font-medium text-blue-800 inline-flex items-center gap-1 hover:underline">
              Open {item.owner_title} <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const GROUP_LABEL = { today: "Today", this_week: "This Week", upcoming: "Upcoming" };

function groupIntoThreeHorizons(horizons) {
  const buckets = { today: [], this_week: [], upcoming: [] };
  for (const h of horizons) {
    const key = h.key === "today" ? "today" : h.key === "this_week" ? "this_week" : "upcoming";
    buckets[key].push(...(h.items || []));
  }
  return ["today", "this_week", "upcoming"]
    .map((key) => ({ key, label: GROUP_LABEL[key], items: buckets[key] }))
    .filter((g) => g.items.length > 0);
}

export default function ExecutionPlan({ plan, onAct, busyId }) {
  const [showResolved, setShowResolved] = useState(false);
  const [assignItem, setAssignItem] = useState(null);
  if (!plan) return null;
  const rawHorizons = (plan.horizons || []).filter((h) => (h.items || []).length > 0);
  const horizons = groupIntoThreeHorizons(rawHorizons);
  const vision = plan.hierarchy?.vision;

  return (
    <div className="space-y-5" data-testid="execution-plan">
      {/* Vision → Today traceability breadcrumb */}
      <div className="rounded-2xl border border-ink-200 bg-gradient-to-br from-ink-950 to-blue-900 p-5 text-white" data-testid="execution-hierarchy">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/60">
          <GitBranch className="w-3.5 h-3.5" /> Execution Hierarchy
        </div>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <Target className="w-4 h-4 text-cyan-300" />
          <span className="text-[17px] font-display font-bold">Vision: {vision?.label || "Operating health"}</span>
        </div>
        <div className="mt-3 flex items-center gap-1.5 flex-wrap text-[11px]">
          {(plan.hierarchy?.levels || []).map((lv, i) => (
            <React.Fragment key={lv.level}>
              {i > 0 && <ChevronRight className="w-3 h-3 text-white/40" />}
              <span className={`px-2 py-1 rounded-md ${lv.items?.length ? "bg-white/10 text-white" : "bg-white/5 text-white/40"}`}>
                {lv.label}{lv.items?.length ? ` · ${lv.items.length}` : ""}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Confidence legend */}
      <div className="flex flex-wrap items-center gap-4 text-[11px] text-ink-500" data-testid="confidence-legend">
        {Object.entries(CONFIDENCE).map(([k, c]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${c.cls}`}><c.Icon className="w-3 h-3" />{c.label}</span>
            <span>{plan.confidence_legend?.[k]}</span>
          </span>
        ))}
      </div>

      {/* Horizon sections */}
      {horizons.map((h) => (
        <div key={h.key} data-testid={`horizon-${h.key}`}>
          <div className="flex items-center justify-between mb-2.5">
            <h3 className="text-[13px] font-semibold uppercase tracking-wider text-ink-500">{h.label}</h3>
            <span className="text-[12px] text-ink-400">{h.items.length} action(s)</span>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {h.items.map((it) => (
              <TaskCard key={it.id} item={it} onAct={onAct} busy={busyId === it.id} onAssign={setAssignItem} />
            ))}
          </div>
        </div>
      ))}

      {horizons.length === 0 && (
        <div className="rounded-xl border border-status-onTrack-border bg-status-onTrack-bg p-6 flex items-center gap-3" data-testid="execution-clear">
          <CheckCircle2 className="w-5 h-5 text-status-onTrack" />
          <p className="text-[14px] text-status-onTrack-text font-medium">No near-term execution items — the roadmap is clear.</p>
        </div>
      )}

      {/* Resolved (completed / deferred) */}
      {plan.resolved?.length > 0 && (
        <div data-testid="execution-resolved">
          <button onClick={() => setShowResolved((v) => !v)}
            className="text-[12px] font-semibold text-ink-500 hover:text-blue-800 inline-flex items-center gap-1"
            data-testid="toggle-resolved">
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showResolved ? "rotate-90" : ""}`} />
            Completed & Deferred ({plan.resolved.length})
          </button>
          {showResolved && (
            <div className="mt-3 grid grid-cols-1 gap-2">
              {plan.resolved.map((it) => (
                <div key={it.id} className="rounded-lg border border-ink-200 bg-ink-50 p-3 flex items-center gap-2" data-testid={`resolved-${it.id}`}>
                  <StatusBadge status={it.status} />
                  <span className="text-[13px] text-ink-700 font-medium">{it.title}</span>
                  <span className="ml-auto text-[11px] text-ink-400">{it.detail}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <AssignTaskModal item={assignItem} onClose={() => setAssignItem(null)} />
    </div>
  );
}
